import { useAppSelector } from '@hooks/useRedux';
import {
  useReviewMonthlyTrend,
  useReviews,
  useReviewStats,
} from '@hooks/accommodation';
import { selectAccommodationProperty } from '@store/accommodation';
import { MessageSquare, Star, TrendingUp, Users } from 'lucide-react';
import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  GuestReviewAnalytics,
  ReviewStats,
} from '../../../../types/accommodation';

// ─── constants ────────────────────────────────────────────────────────────────

const STAR_COLORS = {
  5: '#22c55e',
  4: '#84cc16',
  3: '#f59e0b',
  2: '#f97316',
  1: '#ef4444',
} as const;

const PAGE_SIZE = 15;

// ─── helpers ──────────────────────────────────────────────────────────────────

function StarDisplay({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={
            n <= rating
              ? 'fill-amber-400 text-amber-400'
              : 'fill-gray-200 text-gray-200'
          }
        />
      ))}
    </span>
  );
}

function RatingBar({
  label,
  value,
  max = 5,
}: {
  label: string;
  value: number | null;
  max?: number;
}) {
  if (value === null) return null;
  const pct = Math.round((value / max) * 100);
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-xs text-gray-500">{label}</span>
      <div className="h-2 flex-1 rounded-full bg-gray-100">
        <div
          className="h-2 rounded-full bg-amber-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-xs font-semibold text-gray-700">
        {value.toFixed(1)}
      </span>
    </div>
  );
}

function SentimentGauge({ score }: { score: number | null }) {
  if (score === null) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-gray-400">
        No data yet
      </div>
    );
  }
  // Map 1-5 to 0-180 degrees
  const angle = ((score - 1) / 4) * 180;
  const sentimentLabel =
    score >= 4.5
      ? 'Excellent'
      : score >= 3.5
        ? 'Good'
        : score >= 2.5
          ? 'Average'
          : score >= 1.5
            ? 'Poor'
            : 'Very poor';

  const sentimentColor =
    score >= 4.5
      ? '#22c55e'
      : score >= 3.5
        ? '#84cc16'
        : score >= 2.5
          ? '#f59e0b'
          : score >= 1.5
            ? '#f97316'
            : '#ef4444';

  // SVG gauge (half-circle, 180° arc)
  const cx = 90;
  const cy = 80;
  const r = 65;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const needleX = cx + r * 0.7 * Math.cos(toRad(180 - angle));
  const needleY = cy - r * 0.7 * Math.sin(toRad(180 - angle));

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 180 90" className="w-48">
        {/* Background arc segments */}
        {[
          { start: 0, end: 36, color: '#fecaca' },
          { start: 36, end: 72, color: '#fed7aa' },
          { start: 72, end: 108, color: '#fef08a' },
          { start: 108, end: 144, color: '#bbf7d0' },
          { start: 144, end: 180, color: '#86efac' },
        ].map(({ start, end, color }) => {
          const x1 = cx + r * Math.cos(toRad(180 - start));
          const y1 = cy - r * Math.sin(toRad(180 - start));
          const x2 = cx + r * Math.cos(toRad(180 - end));
          const y2 = cy - r * Math.sin(toRad(180 - end));
          return (
            <path
              key={color}
              d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 0 ${x2} ${y2} Z`}
              fill={color}
              stroke="#fff"
              strokeWidth={1}
            />
          );
        })}
        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={needleX}
          y2={needleY}
          stroke="#1a1a2e"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={5} fill="#1a1a2e" />
      </svg>
      <div className="mt-1 text-center">
        <p className="text-3xl font-bold" style={{ color: sentimentColor }}>
          {score.toFixed(2)}
        </p>
        <p
          className="mt-0.5 text-xs font-semibold uppercase tracking-wide"
          style={{ color: sentimentColor }}
        >
          {sentimentLabel}
        </p>
        <p className="mt-1 text-xs text-gray-400">Overall guest sentiment</p>
      </div>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {label}
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
        </div>
        <div className="rounded-xl bg-gray-50 p-2.5 text-gray-400">{icon}</div>
      </div>
    </div>
  );
}

// ─── Review card ──────────────────────────────────────────────────────────────

function ReviewCard({ review }: { review: GuestReviewAnalytics }) {
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
              {(review.guest_name || 'G').charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {review.guest_name || 'Anonymous guest'}
              </p>
              <p className="text-xs text-gray-400">
                Stayed {fmtDate(review.check_in)} – {fmtDate(review.check_out)}{' '}
                · {review.nights_stayed} night
                {review.nights_stayed !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {review.remarks && (
            <p className="mt-3 text-sm leading-relaxed text-gray-600">
              &quot;{review.remarks}&quot;
            </p>
          )}

          {/* Category breakdown */}
          <div className="mt-3 flex flex-wrap gap-2">
            {review.cleanliness_rating !== null && (
              <span className="rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
                Cleanliness{' '}
                <strong className="text-gray-700">
                  {review.cleanliness_rating}/5
                </strong>
              </span>
            )}
            {review.service_rating !== null && (
              <span className="rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
                Service{' '}
                <strong className="text-gray-700">
                  {review.service_rating}/5
                </strong>
              </span>
            )}
            {review.food_rating !== null && (
              <span className="rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
                Food{' '}
                <strong className="text-gray-700">
                  {review.food_rating}/5
                </strong>
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1">
          <StarDisplay rating={review.overall_rating} size={16} />
          <p className="text-xs text-gray-400">
            {fmtDate(review.submitted_at)}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({
  stats,
  trend,
  isLoadingStats,
  isLoadingTrend,
}: {
  stats: ReviewStats | undefined;
  trend: ReturnType<typeof useReviewMonthlyTrend>['data'];
  isLoadingStats: boolean;
  isLoadingTrend: boolean;
}) {
  const responseRate =
    stats && stats.total_checkouts > 0
      ? Math.round((stats.total_reviews / stats.total_checkouts) * 100)
      : null;

  const fiveStarRatio =
    stats && stats.total_reviews > 0
      ? Math.round((stats.five_star_count / stats.total_reviews) * 100)
      : null;

  const chartData = (trend ?? []).map((t) => ({
    name: t.month_label,
    '5★': t.five_star,
    '4★': t.four_star,
    '3★': t.three_star,
    '2★': t.two_star,
    '1★': t.one_star,
  }));

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total reviews"
          value={stats?.total_reviews ?? (isLoadingStats ? '…' : '0')}
          icon={<MessageSquare size={18} />}
        />
        <StatCard
          label="Avg overall rating"
          value={
            stats?.avg_overall
              ? `${stats.avg_overall.toFixed(1)} / 5`
              : isLoadingStats
                ? '…'
                : '—'
          }
          icon={<Star size={18} />}
        />
        <StatCard
          label="5-star reviews"
          value={stats?.five_star_count ?? (isLoadingStats ? '…' : '0')}
          sub={
            fiveStarRatio !== null
              ? `${fiveStarRatio}% of all reviews`
              : undefined
          }
          icon={<TrendingUp size={18} />}
        />
        <StatCard
          label="Response rate"
          value={
            responseRate !== null
              ? `${responseRate}%`
              : isLoadingStats
                ? '…'
                : '—'
          }
          sub={
            stats
              ? `${stats.total_reviews} of ${stats.total_checkouts} checkouts`
              : undefined
          }
          icon={<Users size={18} />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Rating trend chart */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 lg:col-span-2">
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-900">
              Rating overview
            </h4>
            <p className="text-xs text-gray-400">
              Monthly review distribution by star rating
            </p>
          </div>

          {isLoadingTrend ? (
            <div className="flex h-48 items-center justify-center text-xs text-gray-400">
              Loading…
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-xs text-gray-400">
              No reviews yet
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={chartData}
                  margin={{ top: 4, right: 4, left: -28, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: '1px solid #e5e7eb',
                    }}
                    cursor={{ fill: '#f9fafb' }}
                  />
                  {(['5★', '4★', '3★', '2★', '1★'] as const).map((key) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      radius={key === '5★' ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    >
                      {chartData.map((entry) => (
                        <Cell
                          key={`${key}-${entry.name}`}
                          fill={
                            key === '5★'
                              ? STAR_COLORS[5]
                              : key === '4★'
                                ? STAR_COLORS[4]
                                : key === '3★'
                                  ? STAR_COLORS[3]
                                  : key === '2★'
                                    ? STAR_COLORS[2]
                                    : STAR_COLORS[1]
                          }
                        />
                      ))}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 flex flex-wrap justify-center gap-4">
                {([5, 4, 3, 2, 1] as const).map((n) => (
                  <span
                    key={n}
                    className="flex items-center gap-1.5 text-xs text-gray-500"
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ background: STAR_COLORS[n] }}
                    />
                    {n}-star
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sentiment gauge + category breakdown */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Guest sentiment
            </h4>
            <p className="text-xs text-gray-400">
              Composite score across all categories
            </p>
          </div>

          <SentimentGauge score={stats?.avg_computed ?? null} />

          {stats && stats.total_reviews > 0 && (
            <div className="mt-5 space-y-2.5 border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Category averages
              </p>
              <RatingBar label="Overall" value={stats.avg_overall} />
              <RatingBar label="Cleanliness" value={stats.avg_cleanliness} />
              <RatingBar label="Service" value={stats.avg_service} />
              <RatingBar label="Food" value={stats.avg_food} />
            </div>
          )}
        </div>
      </div>

      {/* Star distribution */}
      {stats && stats.total_reviews > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h4 className="mb-4 text-sm font-semibold text-gray-900">
            Rating breakdown
          </h4>
          <div className="space-y-2">
            {([5, 4, 3, 2, 1] as const).map((n) => {
              const count =
                n === 5
                  ? stats.five_star_count
                  : n === 4
                    ? stats.four_star_count
                    : n === 3
                      ? stats.three_star_count
                      : n === 2
                        ? stats.two_star_count
                        : stats.one_star_count;
              const pct =
                stats.total_reviews > 0
                  ? (count / stats.total_reviews) * 100
                  : 0;
              return (
                <div key={n} className="flex items-center gap-3">
                  <span className="flex w-14 shrink-0 items-center gap-1 text-xs font-medium text-gray-600">
                    <Star size={11} className="fill-amber-400 text-amber-400" />
                    {n} star
                  </span>
                  <div className="h-2.5 flex-1 rounded-full bg-gray-100">
                    <div
                      className="h-2.5 rounded-full transition-all"
                      style={{ width: `${pct}%`, background: STAR_COLORS[n] }}
                    />
                  </div>
                  <span className="w-8 text-right text-xs text-gray-500">
                    {count}
                  </span>
                  <span className="w-10 text-right text-xs text-gray-400">
                    {pct.toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reviews list tab ─────────────────────────────────────────────────────────

function ReviewsListTab({
  reviews,
  total,
  page,
  onPageChange,
  isLoading,
}: {
  reviews: GuestReviewAnalytics[];
  total: number;
  page: number;
  onPageChange: (p: number) => void;
  isLoading: boolean;
}) {
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-gray-400">
        Loading reviews…
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-200 py-20 text-center">
        <Star size={36} className="text-gray-200" />
        <p className="text-sm font-medium text-gray-500">No reviews yet</p>
        <p className="max-w-xs text-xs text-gray-400">
          Reviews appear here once guests submit feedback after checkout. Share
          the review link by ensuring checkout emails are enabled for your
          property.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reviews.map((review) => (
        <ReviewCard key={review.id} review={review} />
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-400">
            Showing {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, total)} of {total} reviews
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'reviews';

export const AccommodationReviewsPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const propertyId = property?.id ?? null;

  const [tab, setTab] = useState<Tab>('overview');
  const [page, setPage] = useState(1);

  const { data: reviewsData, isLoading: isLoadingReviews } = useReviews(
    propertyId,
    page,
    PAGE_SIZE,
  );

  const { data: stats, isLoading: isLoadingStats } = useReviewStats(propertyId);

  const { data: trend, isLoading: isLoadingTrend } =
    useReviewMonthlyTrend(propertyId);

  const reviews = reviewsData?.data ?? [];
  const totalReviews = reviewsData?.total ?? 0;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Guest Reviews
          </h2>
          <p className="mt-0.5 text-xs text-gray-400">
            Feedback collected from guests after checkout
            {property ? ` · ${property.name}` : ''}
          </p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 w-fit">
        {(['overview', 'reviews'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={[
              'rounded-lg px-4 py-1.5 text-sm font-medium transition-colors capitalize',
              tab === t
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            ].join(' ')}
          >
            {t === 'overview' ? 'Overview' : 'All reviews'}
            {t === 'reviews' && totalReviews > 0 && (
              <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                {totalReviews}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <OverviewTab
          stats={stats}
          trend={trend}
          isLoadingStats={isLoadingStats}
          isLoadingTrend={isLoadingTrend}
        />
      ) : (
        <ReviewsListTab
          reviews={reviews}
          total={totalReviews}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isLoadingReviews}
        />
      )}
    </div>
  );
};
